import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

export type CompanyInvitePreview = {
  workspaceName: string;
  companyName: string;
  email: string;
  expired: boolean;
  accepted: boolean;
  emailMatches: boolean;
};
export type CompanyWorkspace = { organizationId: string; name: string; status: 'ACTIVE' | 'SUSPENDED' };
export type MaintenanceCompanyProfile = {
  companyId: string;
  name: string;
  trades: string | null;
  serviceArea: string | null;
  contactEmail: string;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED';
  role: 'ADMIN' | 'TECHNICIAN';
  workspaces: CompanyWorkspace[];
  /** The signed-in person's own name (null until they set one) and email. */
  myName?: string | null;
  myEmail?: string | null;
};

export type CompanyJobStatus = 'OPEN' | 'IN_PROGRESS' | 'SCHEDULED' | 'RESOLVED' | 'CLOSED' | 'CANCELLED';
export type CompanyJob = {
  id: string;
  organizationId: string;
  workspaceName: string;
  title: string;
  description: string | null;
  category: string | null;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | null;
  status: CompanyJobStatus;
  propertyName: string | null;
  propertyAddress: string | null;
  unitLabel: string | null;
  technicianUserId: string | null;
  technicianName: string | null;
  assignedAt: string | null;
  scheduledFor: string | null;
  dueAt: string | null;
  createdDate: string | null;
  /** How to get in (keys, codes, parking, pets), written by the landlord's team. */
  accessNotes: string | null;
  /** Who reported the problem (a tenant or the landlord's team). */
  reportedByName: string | null;
  /** The landlord gave the company this job and it has not accepted it yet. */
  awaitingReply?: boolean;
  /** The landlord workspace's time zone: visit times are shown and booked on its wall clock. */
  workspaceTimeZone?: string | null;
};
export type CompanyTechnician = { id: string; userId: string; name: string; email: string; role: 'ADMIN' | 'TECHNICIAN' };
export type CompanyTeamMember = { id: string; name: string; email: string; role: 'ADMIN' | 'TECHNICIAN'; status: 'INVITED' | 'ACTIVE' | 'REMOVED'; invitationExpired?: boolean };
/** Something the signed-in company person was told (also emailed): a new job, a landlord message, a change. */
export type CompanyNotification = {
  id: string; kind: string; title: string; body: string; actionPath: string | null; requestId: string | null;
  createdDate: string | null; read: boolean;
};
export type CompanyNotificationFeed = { items: CompanyNotification[]; unread: number; hasMore: boolean };

export type CompanyPhotoStage = 'BEFORE' | 'DURING' | 'AFTER';
/** A photo on a job: from the report, or added by a company (with its stage); `ours` = this company's, removable. */
export type CompanyJobPhoto = {
  id: string; fileName: string | null; contentType: string | null;
  stage?: CompanyPhotoStage | null; fromCompany?: boolean; ours?: boolean; addedBy?: string | null; addedAt?: string | null;
};
export type CompanyJobHistoryStep = {
  type: string; status: CompanyJobStatus | null; at: string | null;
  /** Who did it; for a booked visit, when it is and (if it was moved) when it was. Older steps may lack these. */
  by?: string | null; visitAt?: string | null; previousVisitAt?: string | null;
  /** For photos added: how many, and at what stage of the work. */
  photoCount?: number | null; photoStage?: CompanyPhotoStage | null;
};
export type CompanyJobDetail = { job: CompanyJob; photos: CompanyJobPhoto[] };
/** One page of a job's history, newest first. */
export type CompanyJobHistoryPage = { items: CompanyJobHistoryStep[]; total: number; page: number; hasMore: boolean };
export type CompanyInvoiceStatus = 'SUBMITTED' | 'APPROVED' | 'BILLED' | 'REJECTED';
export type CompanyInvoiceLine = { lineNo: number; description: string; quantity: number; unitPrice: number; amount: number };
export type CompanyInvoice = {
  id: string; invoiceNumber: string; currency: string; total: number; notes: string | null; status: CompanyInvoiceStatus;
  submittedByName: string | null; submittedAt: string | null; decidedAt: string | null; rejectionReason: string | null;
  lines: CompanyInvoiceLine[];
};
export type CompanyInvoiceInput = {
  invoiceNumber: string; notes: string | null; lines: { description: string; quantity: number; unitPrice: number }[];
};
/** `fromCompany`: written by someone in the company; `mine`: by the signed-in person. */
export type CompanyJobMessage = { id: string; authorName: string | null; body: string; createdDate: string | null; fromCompany: boolean; mine: boolean };

const PENDING_INVITE_KEY = 'apexMaintenanceCompanyPendingInvite';

/**
 * A maintenance company's side of Apex: reading and accepting a landlord's invitation, and the company's own profile.
 * An invitation opened before its account exists is remembered in this browser, so signing in afterwards comes back
 * to it.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceCompanyService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiGatewayUrl}/maintenance-service`;

  preview(token: string): Observable<CompanyInvitePreview> {
    return this.http.get<CompanyInvitePreview>(`${this.base}/api/v1/public/company-invitations/${encodeURIComponent(token)}`,
      { withCredentials: true });
  }

  accept(token: string): Observable<MaintenanceCompanyProfile> {
    return this.http.post<MaintenanceCompanyProfile>(`${this.base}/company-invitations/${encodeURIComponent(token)}/accept`, {},
      { withCredentials: true });
  }

  /** The signed-in person's notifications, newest first, with how many are unread. */
  notifications(page = 0, size = 20): Observable<CompanyNotificationFeed> {
    return this.http.get<CompanyNotificationFeed>(`${this.base}/company/notifications`, { params: { page, size }, withCredentials: true });
  }

  unreadNotifications(): Observable<{ unread: number }> {
    return this.http.get<{ unread: number }>(`${this.base}/company/notifications/unread-count`, { withCredentials: true });
  }

  markNotificationRead(id: string): Observable<CompanyNotification> {
    return this.http.post<CompanyNotification>(`${this.base}/company/notifications/${encodeURIComponent(id)}/read`, {}, { withCredentials: true });
  }

  markAllNotificationsRead(): Observable<void> {
    return this.http.post<void>(`${this.base}/company/notifications/read-all`, {}, { withCredentials: true });
  }

  /** The company's admin takes on a job the landlord gave it. */
  acceptJob(jobId: string): Observable<CompanyJob> {
    return this.http.post<CompanyJob>(`${this.base}/company/jobs/${encodeURIComponent(jobId)}/accept`, {}, { withCredentials: true });
  }

  /** The company's admin turns a job down (before any work), with a reason the landlord sees. */
  declineJob(jobId: string, reason: string): Observable<void> {
    return this.http.post<void>(`${this.base}/company/jobs/${encodeURIComponent(jobId)}/decline`, { reason }, { withCredentials: true });
  }

  /** Sets the name landlords and the team see for the signed-in person. */
  updateMyName(name: string): Observable<MaintenanceCompanyProfile> {
    return this.http.put<MaintenanceCompanyProfile>(`${this.base}/company/me/name`, { name }, { withCredentials: true });
  }

  myCompany(): Observable<MaintenanceCompanyProfile> {
    return this.http.get<MaintenanceCompanyProfile>(`${this.base}/company/me`, { withCredentials: true });
  }

  browseJobs(filters: { page?: number; size?: number; q?: string; view?: string; landlord?: string | null;
                        technician?: string | null; priority?: string | null; from?: string; to?: string } = {}) {
    const params: Record<string, string | number> = { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
    for (const [key, value] of Object.entries(filters)) if (value != null) params[key] = value;
    return this.http.get<{ content: CompanyJob[]; totalElements: number; number: number; size: number;
      counts: Record<string, number>; workspaces: Record<string, string>; technicianCounts: Record<string, number> }>(
      `${this.base}/company/jobs/browse`, { params, withCredentials: true });
  }

  jobs(): Observable<CompanyJob[]> {
    return this.http.get<CompanyJob[]>(`${this.base}/company/jobs`, { withCredentials: true });
  }

  job(jobId: string): Observable<CompanyJobDetail> {
    return this.http.get<CompanyJobDetail>(`${this.base}/company/jobs/${encodeURIComponent(jobId)}`, { withCredentials: true });
  }

  /** Uploads before, during or after photos of the work (images only, at most 10 at a time). */
  addPhotos(jobId: string, stage: CompanyPhotoStage, files: File[]): Observable<CompanyJobPhoto[]> {
    const form = new FormData();
    files.forEach(file => form.append('files', file, file.name));
    return this.http.post<CompanyJobPhoto[]>(`${this.base}/company/jobs/${encodeURIComponent(jobId)}/photos`, form,
      { params: { stage }, withCredentials: true });
  }

  /** Removes a photo the company added. */
  removePhoto(jobId: string, photoId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/company/jobs/${encodeURIComponent(jobId)}/photos/${encodeURIComponent(photoId)}`,
      { withCredentials: true });
  }

  /** A job's history, newest first, a page at a time. */
  jobHistory(jobId: string, page = 0, size = 10): Observable<CompanyJobHistoryPage> {
    return this.http.get<CompanyJobHistoryPage>(`${this.base}/company/jobs/${encodeURIComponent(jobId)}/history`,
      { params: { page, size }, withCredentials: true });
  }

  /** The browser follows this (with the session cookie) to a short-lived link for the photo. */
  photoUrl(jobId: string, photoId: string): string {
    return `${this.base}/company/jobs/${encodeURIComponent(jobId)}/photos/${encodeURIComponent(photoId)}/content`;
  }

  /** Books the visit at a wall-clock time (yyyy-MM-ddTHH:mm, no offset) in the job's workspace time zone. */
  scheduleVisit(jobId: string, scheduledForLocal: string): Observable<CompanyJob> {
    return this.http.put<CompanyJob>(`${this.base}/company/jobs/${jobId}/visit`, { scheduledForLocal }, { withCredentials: true });
  }

  invoices(jobId: string): Observable<CompanyInvoice[]> {
    return this.http.get<CompanyInvoice[]>(`${this.base}/company/jobs/${encodeURIComponent(jobId)}/invoices`, { withCredentials: true });
  }

  submitInvoice(jobId: string, input: CompanyInvoiceInput): Observable<CompanyInvoice> {
    return this.http.post<CompanyInvoice>(`${this.base}/company/jobs/${encodeURIComponent(jobId)}/invoices`, input, { withCredentials: true });
  }

  technicians(): Observable<CompanyTechnician[]> {
    return this.http.get<CompanyTechnician[]>(`${this.base}/company/technicians`, { withCredentials: true });
  }

  assignTechnician(jobId: string, memberId: string): Observable<CompanyJob> {
    return this.http.put<CompanyJob>(`${this.base}/company/jobs/${jobId}/technician`, { memberId }, { withCredentials: true });
  }

  updateJob(jobId: string, status: CompanyJobStatus | null, note: string | null): Observable<CompanyJob> {
    return this.http.put<CompanyJob>(`${this.base}/company/jobs/${jobId}`, { status, note }, { withCredentials: true });
  }

  messages(jobId: string): Observable<CompanyJobMessage[]> {
    return this.http.get<CompanyJobMessage[]>(`${this.base}/company/jobs/${jobId}/messages`, { withCredentials: true });
  }

  team(): Observable<CompanyTeamMember[]> {
    return this.http.get<CompanyTeamMember[]>(`${this.base}/company/team`, { withCredentials: true });
  }

  inviteTechnician(name: string, email: string): Observable<CompanyTeamMember> {
    return this.http.post<CompanyTeamMember>(`${this.base}/company/team`, { name, email }, { withCredentials: true });
  }

  removeMember(memberId: string): Observable<CompanyTeamMember> {
    return this.http.put<CompanyTeamMember>(`${this.base}/company/team/${memberId}/remove`, {}, { withCredentials: true });
  }

  rememberInvite(token: string): void {
    try { window.localStorage.setItem(PENDING_INVITE_KEY, token); } catch { /* storage unavailable: the email link still works */ }
  }

  pendingInvite(): string | null {
    try { return window.localStorage.getItem(PENDING_INVITE_KEY); } catch { return null; }
  }

  forgetInvite(): void {
    try { window.localStorage.removeItem(PENDING_INVITE_KEY); } catch { /* nothing to forget */ }
  }
}
